var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import './ColumnsModal.scss';
import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { SonarModal } from '@ui/modals';
import { SonarText } from '@ui/text';
import { Button } from 'antd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { EyeFilled, EyeInvisibleFilled, LockFilled } from '@ant-design/icons';
import { getColumnsString, parseColumnString } from '@ui/table-modifier/@arch/lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export const ColumnsModal = (_a) => {
    var { columns, columnsString, onCancel, onChange } = _a, rest = __rest(_a, ["columns", "columnsString", "onCancel", "onChange"]);
    const [dropDestination, setDropDestination] = useState(-1);
    const [columnIds, setColumnIds] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState({});
    const originalColumnIdsRef = useRef([]);
    const originalColumnVisibilityRef = useRef({});
    useEffect(() => {
        const { ids, isVisible } = parseColumnString(columnsString ? columnsString : getColumnsString(columns.map((column) => column.id)));
        originalColumnIdsRef.current = ids;
        originalColumnVisibilityRef.current = isVisible;
        setColumnIds(ids);
        setVisibleColumns(Object.assign({}, isVisible));
    }, [columns, columnsString]);
    const columnNames = useMemo(() => columns.reduce((names, column) => {
        names[column.id] = column.name;
        return names;
    }, {}), [columns]);
    const handleHideColumn = useCallback((id) => {
        setVisibleColumns((prevState) => (Object.assign(Object.assign({}, prevState), { [id]: !prevState[id] })));
    }, [setVisibleColumns]);
    const handleOnCancel = useCallback(() => {
        setColumnIds(originalColumnIdsRef.current);
        setVisibleColumns(originalColumnVisibilityRef.current);
        onCancel();
    }, [onCancel]);
    const handleOnUpdate = useCallback(() => {
        const columnsStr = getColumnsString(columnIds, visibleColumns);
        onChange({ columns: columnsStr });
        onCancel();
    }, [columnIds, onCancel, onChange, visibleColumns]);
    const handleDragChange = useCallback((dropResult) => {
        var _a, _b;
        const source = dropResult.source.index;
        const destination = (_b = (_a = dropResult.destination) === null || _a === void 0 ? void 0 : _a.index) !== null && _b !== void 0 ? _b : -1;
        const index = destination < source ? destination : destination + 1;
        setDropDestination(index);
    }, []);
    const handleReorderColumns = useCallback((dropResult) => {
        setDropDestination(-1);
        const { source, destination } = dropResult;
        if (!destination) {
            return;
        }
        setColumnIds((arr) => {
            const entry = arr[source.index];
            const newOrder = [...arr.slice(0, source.index), ...arr.slice(source.index + 1)];
            newOrder.splice(destination.index, 0, entry);
            return newOrder;
        });
    }, []);
    return (React.createElement(SonarModal, Object.assign({ centered: true, className: 'data-dictionary-columns-modal', footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: handleOnCancel }, COPY_SPECIFIC.CANCEL),
            React.createElement(Button, { onClick: handleOnUpdate, type: 'primary' }, COPY_SPECIFIC.UPDATE)), maskClosable: false, onCancel: handleOnCancel, title: COPY_SPECIFIC.COLUMNS, width: 300 }, rest),
        React.createElement(DragDropContext, { onDragEnd: handleReorderColumns, onDragStart: handleDragChange, onDragUpdate: handleDragChange },
            React.createElement(Droppable, { droppableId: 'columns' }, (provided) => React.createElement("div", Object.assign({ ref: provided.innerRef }, provided.droppableProps),
                columnIds.map((columnId, index) => {
                    if (index === 0) {
                        return (React.createElement("div", { className: 'column-card', key: columnId },
                            React.createElement(SonarText, null, columnNames[columnId]),
                            React.createElement(LockFilled, null)));
                    }
                    return (React.createElement(Draggable, { draggableId: String(columnId), index: index, isDragDisabled: index === 0, key: columnId }, (draggableProvided, dragSnapshot) => React.createElement("div", Object.assign({ className: classNames('column-card', {
                            dragging: dragSnapshot.isDragging,
                            drop: dropDestination === index,
                            hidden: !visibleColumns[columnId]
                        }), ref: draggableProvided.innerRef }, draggableProvided.draggableProps, draggableProvided.dragHandleProps),
                        React.createElement(SonarText, null, columnNames[columnId]),
                        React.createElement("div", { className: 'hide-column-toggle', onClick: handleHideColumn.bind(null, columnId) }, visibleColumns[columnId] ?
                            React.createElement(EyeFilled, null) :
                            React.createElement(EyeInvisibleFilled, null)))));
                }),
                provided.placeholder && React.createElement("div", { className: 'drag-placeholder' }, provided.placeholder))))));
};
